import React, { useState, useEffect, useCallback } from 'react';
import {
    Star, StarBorder, Visibility, VisibilityOff,
    Person, CalendarMonth, LocalActivity, RateReview, Search, Refresh,
    CheckCircle, HourglassEmpty, DeleteOutline, Reply
} from '@mui/icons-material';
import {
    Container, Typography, Box, Paper, Grid, Card, CardContent,
    Chip, Button, IconButton, useTheme, useMediaQuery, Divider,
    Pagination, Stack, TextField, InputAdornment
} from '@mui/material';
import welfareService from '../../utils/welfareService';
import Loader from '../../components/Loader';
import { snackbar, confirm } from '../../utils/showMessage';

const RatingsManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [replyDrafts, setReplyDrafts] = useState({});
    const [replyLoading, setReplyLoading] = useState(null);

    const loadRatings = useCallback(async () => {
        try {
            setLoading(true);
            const data = await welfareService.getAllRatings(page, 20);
            setRatings(data.ratings);
            setReplyDrafts(Object.fromEntries((data.ratings || []).map((r) => [r._id, r.adminReply || ''])));
            setTotalPages(data.totalPages);
            setTotal(data.total);
        } catch (error) {
            console.error('Error loading ratings:', error);
            snackbar('خطا در بارگذاری نظرات');
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        loadRatings();
    }, [loadRatings]);

    const handleToggleStatus = async (rating) => {
        try {
            await welfareService.toggleRatingStatus(rating._id);
            snackbar(rating.isActive ? 'نظر از حالت نمایش خارج شد' : 'نظر تأیید و منتشر شد');
            loadRatings();
        } catch (error) {
            console.error('Error toggling rating status:', error);
            snackbar('خطا در تغییر وضعیت نظر');
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirm('این نظر برای همیشه حذف شود؟ این عمل قابل بازگشت نیست.', 'حذف کن', 'انصراف');
        if (!ok) return;
        try {
            await welfareService.adminDeleteRating(id);
            snackbar('نظر حذف شد');
            loadRatings();
        } catch (error) {
            console.error('Error deleting rating:', error);
            const status = error?.response?.status;
            const msg = error?.response?.data?.message || error?.message;
            snackbar(`خطا در حذف نظر${status ? ` (کد ${status})` : ''}${msg ? ` — ${msg}` : ''}`);
        }
    };

    const handleSaveReply = async (id, valueOverride) => {
        const value = valueOverride !== undefined ? valueOverride : (replyDrafts[id] ?? '');
        setReplyLoading(id);
        try {
            await welfareService.replyToRating(id, value);
            snackbar(value.trim() ? 'پاسخ ثبت شد' : 'پاسخ حذف شد');
            loadRatings();
        } catch (error) {
            console.error('Error saving reply:', error);
            const status = error?.response?.status;
            const msg = error?.response?.data?.message || error?.message;
            snackbar(`خطا در ثبت پاسخ${status ? ` (کد ${status})` : ''}${msg ? ` — ${msg}` : ''}`);
        } finally {
            setReplyLoading(null);
        }
    };

    const renderStars = (rating) => {
        const stars = [];
        // Show max 5 stars on mobile to save space
        const maxStars = isMobile ? 5 : 10;
        const displayRating = isMobile ? Math.ceil(rating / 2) : rating;
        
        for (let i = 1; i <= maxStars; i++) {
            stars.push(
                <Star 
                    key={i} 
                    fontSize="small" 
                    sx={{ color: i <= displayRating ? '#ffc107' : 'grey.300', fontSize: isMobile ? '16px' : '20px' }} 
                />
            );
        }
        return stars;
    };

    if (loading && page === 1) {
        return <Loader />;
    }

    return (
        <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
            {/* Header */}
            <Box 
                display="flex" 
                flexDirection={isMobile ? 'column' : 'row'} 
                justifyContent="space-between" 
                alignItems={isMobile ? 'stretch' : 'center'} 
                gap={2}
                mb={4}
            >
                <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">مدیریت نظرات و امتیازات</Typography>
                    <Typography variant="body2" color="text.secondary">مجموع {total} نظر — نظرها فقط پس از «تأیید» شما برای کاربران نمایش داده می‌شوند</Typography>
                </Box>
                <IconButton onClick={loadRatings} sx={{ bgcolor: 'grey.100', alignSelf: isMobile ? 'flex-end' : 'center' }}>
                    <Refresh fontSize="small" />
                </IconButton>
            </Box>

            {/* Ratings List */}
            <Grid container spacing={isMobile ? 2 : 3}>
                {ratings.length === 0 ? (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
                            <RateReview sx={{ fontSize: 48, color: 'disabled.main', mb: 2, opacity: 0.5 }} />
                            <Typography color="text.secondary">هنوز نظری ثبت نشده است</Typography>
                        </Paper>
                    </Grid>
                ) : (
                    ratings.map((rating) => (
                        <Grid item xs={12} key={rating._id}>
                            <Card sx={{ 
                                borderRadius: 4, 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                borderLeft: '5px solid',
                                borderLeftColor: rating.isActive ? 'success.main' : 'grey.400',
                                position: 'relative',
                                overflow: 'visible'
                            }}>
                                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8}>
                                            {/* Service Info */}
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <LocalActivity color="primary" fontSize="small" />
                                                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                                    {rating.serviceId?.title || 'خدمت حذف شده'}
                                                </Typography>
                                            </Box>

                                            {/* User Info */}
                                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                <Person fontSize="inherit" color="disabled" />
                                                <Typography variant="body2" fontWeight="medium">
                                                    {rating.userId?.firstName} {rating.userId?.lastName}
                                                </Typography>
                                                {rating.userId?.medicalSystemNumber && (
                                                    <Chip label={rating.userId.medicalSystemNumber} size="small" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                                                )}
                                            </Box>

                                            {/* Rating & Stars */}
                                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                                <Box display="flex">{renderStars(rating.rating)}</Box>
                                                <Chip
                                                    label={`${rating.rating}/10`}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    icon={rating.isActive ? <CheckCircle fontSize="small" /> : <HourglassEmpty fontSize="small" />}
                                                    label={rating.isActive ? 'تأیید شده' : 'در انتظار تأیید'}
                                                    color={rating.isActive ? 'success' : 'warning'}
                                                />
                                            </Box>

                                            {/* Comment */}
                                            {rating.comment && (
                                                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 2, mb: 2 }}>
                                                    <Typography variant="body2" sx={{ lineHeight: 1.6, textAlign: 'justify' }}>
                                                        {rating.comment}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Date */}
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <CalendarMonth fontSize="inherit" color="disabled" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(rating.createdAt).toLocaleDateString('fa-IR', {
                                                        year: 'numeric', month: 'long', day: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })}
                                                </Typography>
                                            </Box>

                                            {/* Admin Reply */}
                                            <Box mt={2}>
                                                <Typography variant="caption" fontWeight="bold" color="primary" display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                    <Reply fontSize="small" /> پاسخ نظام پزشکی (زیر نظر برای همه نمایش داده می‌شود)
                                                </Typography>
                                                <TextField
                                                    value={replyDrafts[rating._id] ?? ''}
                                                    onChange={(e) => setReplyDrafts((p) => ({ ...p, [rating._id]: e.target.value }))}
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    size="small"
                                                    placeholder="پاسخ خود را بنویسید..."
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' } }}
                                                />
                                                <Box display="flex" gap={1} mt={1}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<Reply />}
                                                        disabled={replyLoading === rating._id}
                                                        onClick={() => handleSaveReply(rating._id)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        {replyLoading === rating._id ? 'در حال ثبت...' : (rating.adminReply ? 'به‌روزرسانی پاسخ' : 'ثبت پاسخ')}
                                                    </Button>
                                                    {rating.adminReply && (
                                                        <Button
                                                            variant="text"
                                                            color="error"
                                                            size="small"
                                                            disabled={replyLoading === rating._id}
                                                            onClick={() => { setReplyDrafts((p) => ({ ...p, [rating._id]: '' })); handleSaveReply(rating._id, ''); }}
                                                        >
                                                            حذف پاسخ
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                                            <Button
                                                variant={rating.isActive ? "outlined" : "contained"}
                                                color={rating.isActive ? "inherit" : "success"}
                                                fullWidth
                                                startIcon={rating.isActive ? <HourglassEmpty /> : <CheckCircle />}
                                                onClick={() => handleToggleStatus(rating)}
                                                sx={{ borderRadius: 3 }}
                                            >
                                                {rating.isActive ? 'لغو انتشار' : 'تأیید و انتشار'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                fullWidth
                                                startIcon={<DeleteOutline />}
                                                onClick={() => handleDelete(rating._id)}
                                                sx={{ borderRadius: 3 }}
                                            >
                                                حذف نظر
                                            </Button>
                                            <Typography variant="caption" align="center" color="text.secondary" display="block">
                                                {rating.isActive ? 'این نظر تأیید شده و برای همه نمایش داده می‌شود' : 'در انتظار تأیید — برای کاربران نمایش داده نمی‌شود'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={5}>
                    <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={(e, v) => setPage(v)} 
                        color="primary" 
                        variant="outlined"
                        shape="rounded"
                        size={isMobile ? "small" : "medium"}
                    />
                </Box>
            )}
        </Container>
    );
};

export default RatingsManagement;

 
